import { useState, forwardRef, useEffect } from "react";
import {
  Autocomplete,
  Button,
  TextField,
  Dialog,
  DialogContent,
  Grid,
  Typography,
  CircularProgress,
  Divider,
  Box,
  Stack,
  Tooltip,
  IconButton,
} from "@mui/material";
import Slide from "@mui/material/Slide";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import {
  TransferOrder,
  resetForm,
  setDialog,
} from "../../../../app/slices/transferOrderSlice";
import { SelectUser } from "../../../../components/Selectors/SelectUser";
import { SelectProducts } from "../../../../components/Selectors/SelectProducts";
import { NumericFormat } from "react-number-format";
import { BiTrash } from "react-icons/bi";
import { num } from "../../../../helpers/invoiceTotals";

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const TransferOrderForm = () => {
  const dispatch = useDispatch();

  const _TransferOrder = useSelector(
    (state) => state.transferOrder.transferOrder
  );
  const _SearchBody = useSelector((state) => state.transferOrder.searchBody);
  const _Users = useSelector((state) => state.user.usersList.data);
  const _Products = useSelector((state) => state.product.products.data);

  const [loading, setLoading] = useState(false);

  const { control, reset, handleSubmit, setValue, watch } = useForm({
    defaultValues: _TransferOrder.form,
  });

  const { fields, append, remove } = useFieldArray({ control, name: "items" });

  const onSubmit = (data) => {
    setLoading(true);
    dispatch(
      TransferOrder.create(
        {
          from_user_id: data.from_user_id,
          to_user_id: data.to_user_id,
          note: data.note,
          items: data.items.map((item) => ({
            product_id: item.product_id,
            quantity: num(item.quantity),
            serial_numbers: item.serial_numbers ?? [],
          })),
        },
        _SearchBody
      )
    )
      .then(() => setLoading(false))
      .catch(() => setLoading(false));
  };

  useEffect(() => {
    reset({ ..._TransferOrder.form });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [_TransferOrder.form]);

  useEffect(() => {
    return () => {
      dispatch(resetForm());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isSerialized = (index) => {
    const productId = watch(`items[${index}].product_id`);
    const product = _Products.find((x) => x.id === productId);
    if (product) return product.is_serialized;
    return (watch(`items[${index}].serial_numbers`) ?? []).length > 0;
  };

  return (
    <Dialog
      open={_TransferOrder.dialog}
      TransitionComponent={Transition}
      keepMounted
      fullWidth={true}
      dir="rtl"
      maxWidth="lg"
      onClose={() => dispatch(setDialog())}
      aria-describedby="alert-dialog-slide-description"
    >
      <DialogContent>
        <Typography
          mb={3}
          variant="h4"
          color={"purple"}
          gutterBottom
          component="div"
        >
          أضافة مناقلة
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="from_user_id"
                control={control}
                render={({ field }) => (
                  <SelectUser
                    requird
                    label="من"
                    onChange={(e, newValue) => {
                      setValue("from_user_id", newValue ? newValue.id : null);
                    }}
                    value={_Users.filter((x) => x.id === field.value)[0] ?? null}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="to_user_id"
                control={control}
                render={({ field }) => (
                  <SelectUser
                    requird
                    label="الى"
                    onChange={(e, newValue) => {
                      setValue("to_user_id", newValue ? newValue.id : null);
                    }}
                    value={_Users.filter((x) => x.id === field.value)[0] ?? null}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Controller
                name="note"
                control={control}
                render={({ field }) => (
                  <TextField
                    size="small"
                    label="الملاحظات"
                    fullWidth
                    multiline
                    rows={2}
                    inputProps={{ maxLength: 255 }}
                    {...field}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={2}>
                {fields.map((item, index) => {
                  const serials = watch(`items[${index}].serial_numbers`) ?? [];
                  const quantity = num(watch(`items[${index}].quantity`));
                  return (
                    <Grid item xs={12} key={item.id}>
                      <Box
                        sx={{
                          p: 2,
                          border: 1,
                          borderColor: "divider",
                          borderRadius: 1,
                        }}
                      >
                        <Stack
                          direction="row"
                          sx={{
                            justifyContent: "space-between",
                            alignItems: "center",
                            mb: 1,
                          }}
                        >
                          <Typography variant="subtitle2" color="text.secondary">
                            {`المادة ${index + 1}`}
                          </Typography>
                          <Tooltip title="حذف المادة">
                            <IconButton
                              color="error"
                              size="small"
                              onClick={() => remove(index)}
                            >
                              <BiTrash />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={8}>
                            <Controller
                              control={control}
                              name={`items[${index}].product_id`}
                              defaultValue={item.product_id}
                              render={({ field }) => (
                                <SelectProducts
                                  required
                                  onChange={(e, newValue) => {
                                    setValue(
                                      `items[${index}].product_id`,
                                      newValue ? newValue.id : null
                                    );
                                    if (!newValue?.is_serialized)
                                      setValue(
                                        `items[${index}].serial_numbers`,
                                        []
                                      );
                                  }}
                                  value={
                                    _Products.filter(
                                      (x) => x.id === field.value
                                    )[0] ?? null
                                  }
                                />
                              )}
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <Controller
                              control={control}
                              name={`items[${index}].quantity`}
                              defaultValue={item.quantity}
                              render={({ field }) => (
                                <NumericFormat
                                  thousandSeparator
                                  customInput={TextField}
                                  value={field.value}
                                  onChange={(e) =>
                                    setValue(
                                      `items[${index}].quantity`,
                                      num(e.target.value)
                                    )
                                  }
                                  valueIsNumericString
                                  allowNegative={false}
                                  fullWidth
                                  size="small"
                                  label="العدد"
                                />
                              )}
                            />
                          </Grid>
                          {isSerialized(index) ? (
                            <Grid item xs={12}>
                              <Controller
                                control={control}
                                name={`items[${index}].serial_numbers`}
                                defaultValue={item.serial_numbers ?? []}
                                render={({ field }) => (
                                  <Autocomplete
                                    multiple
                                    freeSolo
                                    options={[]}
                                    value={field.value ?? []}
                                    onChange={(e, newValue) =>
                                      setValue(
                                        `items[${index}].serial_numbers`,
                                        newValue
                                      )
                                    }
                                    renderInput={(params) => (
                                      <TextField
                                        {...params}
                                        size="small"
                                        label="الارقام التسلسلية"
                                        helperText={
                                          serials.length === quantity
                                            ? `${serials.length}/${quantity}`
                                            : `عدد الارقام التسلسلية ${serials.length} والعدد ${quantity}`
                                        }
                                        error={serials.length !== quantity}
                                      />
                                    )}
                                  />
                                )}
                              />
                            </Grid>
                          ) : null}
                        </Grid>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="contained"
                color="primary"
                onClick={() =>
                  append({ product_id: null, quantity: 1, serial_numbers: [] })
                }
              >
                اضافة منتج جديد
              </Button>
            </Grid>
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={loading ? true : false}
                endIcon={loading ? <CircularProgress size={25} /> : null}
              >
                حفظ
              </Button>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default TransferOrderForm;
